import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, patch, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import EmptyState from '../components/widgets/EmptyState';
import { fmtBRL, fmtDate, todayISO } from '../lib/format';
import { Plus, Trash2, Filter, X, ListChecks, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const TYPES = [
  { value: 'RECEITA', label: 'Receitas', color: '#10B981' },
  { value: 'CUSTO', label: 'Custos', color: '#F59E0B' },
  { value: 'DESPESA', label: 'Despesas', color: '#EF4444' },
  { value: 'INVESTIMENTO', label: 'Investimentos', color: '#3B82F6' },
  { value: 'IMPOSTO', label: 'Impostos', color: '#8B5CF6' },
  { value: 'RETIRADA_LUCRO', label: 'Retirada de Lucro', color: '#EC4899' },
];

const blank = () => ({
  type: 'RECEITA',
  company_id: '',
  project_id: '',
  cost_center_id: '',
  category_id: '',
  description: '',
  amount: '',
  date: todayISO(),
  competence: todayISO().slice(0, 7),
  status: 'PAGO',
  realized: true,  // true = Realizado, false = Provisionado (cria Receivable/Payable)
  notes: '',
  expense_kind: '',  // FIXA | VARIAVEL (aparece só quando type=DESPESA)
  // Rateio
  is_allocated: false,
  allocation_mode: 'PERCENT', // PERCENT ou AMOUNT
  parts: [],  // [{company_id, percent, amount}]
});

export default function Lancamentos() {
  const { companies, projects, costCenters, categories, competence, companyFilter, isLocked } = useApp();
  const [tab, setTab] = useState('RECEITA');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [editId, setEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      const list = await get('/entries', {
        type: tab,
        competence,
        ...(cid ? { company_id: cid } : {}),
      });
      setEntries(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab, competence, companyFilter]);

  const openNew = () => {
    setEditId(null);
    setForm({ ...blank(), type: tab, competence });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      type: e.type,
      company_id: e.company_id,
      project_id: e.project_id || '',
      cost_center_id: e.cost_center_id || '',
      category_id: e.category_id || '',
      description: e.description,
      amount: String(e.amount),
      date: e.date,
      competence: e.competence,
      status: e.status,
      notes: e.notes || '',
      expense_kind: e.expense_kind || '',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.description.trim()) return toast.error('Informe a descrição');
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!amount || amount <= 0) return toast.error('Valor inválido');

    // Caminho 1: RATEIO
    if (form.is_allocated && !editId) {
      if (!form.parts || form.parts.length < 2) {
        return toast.error('O rateio precisa de ao menos 2 empresas');
      }
      const allParts = form.parts.filter((p) => p.company_id);
      if (allParts.length !== form.parts.length) {
        return toast.error('Cada parte do rateio precisa ter uma empresa');
      }
      const ids = allParts.map((p) => p.company_id);
      if (new Set(ids).size !== ids.length) {
        return toast.error('Cada empresa só pode aparecer 1 vez no rateio');
      }
      // Valida soma
      if (form.allocation_mode === 'PERCENT') {
        const totalPct = allParts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0);
        if (Math.abs(totalPct - 100) > 0.01) {
          return toast.error(`Soma dos percentuais = ${totalPct.toFixed(2)}% (precisa ser 100%)`);
        }
      } else {
        const totalAmt = allParts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        if (Math.abs(totalAmt - amount) > 0.01) {
          return toast.error(`Soma dos valores R$ ${totalAmt.toFixed(2)} ≠ total R$ ${amount.toFixed(2)}`);
        }
      }
      const payload = {
        type: form.type,
        description: form.description,
        total_amount: amount,
        date: form.date,
        competence: form.competence,
        status: form.status,
        category_id: form.category_id || null,
        cost_center_id: form.cost_center_id || null,
        project_id: form.project_id || null,
        notes: form.notes || null,
        parts: allParts.map((p) => ({
          company_id: p.company_id,
          percent: form.allocation_mode === 'PERCENT' ? Number(p.percent) : null,
          amount: form.allocation_mode === 'AMOUNT' ? Number(p.amount) : null,
        })),
      };
      try {
        const r = await post('/entries/allocated', payload);
        toast.success(`Rateio criado em ${r.parts_count} empresas`);
        setOpen(false);
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erro ao criar rateio');
      }
      return;
    }

    // Caminho 2: lançamento normal
    if (!form.company_id) return toast.error('Selecione a empresa');

    const payload = {
      ...form,
      amount,
      project_id: form.project_id || null,
      cost_center_id: form.cost_center_id || null,
      category_id: form.category_id || null,
      notes: form.notes || null,
      // expense_kind só faz sentido em DESPESA — envia null caso contrário
      expense_kind: form.type === 'DESPESA' && form.expense_kind ? form.expense_kind : null,
    };
    delete payload.is_allocated;
    delete payload.allocation_mode;
    delete payload.parts;

    try {
      if (editId) {
        const { realized, ...patchPayload } = payload;
        await patch(`/entries/${editId}`, patchPayload);
        toast.success('Lançamento atualizado');
      } else {
        await post('/entries', payload);
        toast.success(form.realized ? 'Lançamento Realizado criado' : 'Provisionamento criado (Conta a Pagar/Receber)');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (item) => {
    // Aceita objeto entry (com kind) ou ID (backward compat)
    const isObj = typeof item === 'object' && item !== null;
    const id = isObj ? item.id : item;
    const kind = isObj ? item.kind : 'realizado';
    const rid = isObj ? item.receivable_id : null;
    const pid = isObj ? item.payable_id : null;
    if (!window.confirm(kind === 'provisionado' ? 'Excluir este provisionamento (Conta a Pagar/Receber)?' : 'Excluir este lançamento?')) return;
    try {
      if (kind === 'provisionado') {
        if (rid) await del(`/receivables/${rid}`);
        else if (pid) await del(`/payables/${pid}`);
        else await del(`/entries/${id}`);
      } else {
        await del(`/entries/${id}`);
      }
      toast.success('Excluído');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const total = entries.reduce((acc, e) => acc + e.amount, 0);
  const filteredProjects = form.company_id ? projects.filter((p) => p.company_id === form.company_id) : [];
  const filteredCC = form.company_id ? costCenters.filter((c) => c.company_id === form.company_id) : [];
  const filteredCats = categories.filter((c) => c.type === form.type);

  return (
    <div className="fade-in">
      <TopBar
        title="Lançamentos Financeiros"
        subtitle="Receitas, Custos, Despesas, Investimentos e Impostos"
        action={
          <Button
            onClick={openNew}
            className="btn-gold hover:btn-gold rounded-md text-sm font-medium gap-1.5 px-4"
            data-testid="btn-new-entry"
          >
            <Plus size={15} /> Novo Lançamento
          </Button>
        }
      />
      <div className="px-8 py-7 space-y-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1" data-testid="entries-tabs">
            {TYPES.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 text-xs px-4"
                data-testid={`tab-${t.value}`}
              >
                <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: t.color }} />
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <SectionCard
          title={`${TYPES.find((t) => t.value === tab)?.label} de ${competence}`}
          subtitle={`${entries.length} lançamentos | Total: ${fmtBRL(total)}`}
          padded={false}
          testid="entries-table"
        >
          {entries.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={ListChecks}
                title="Nenhum lançamento encontrado"
                description={`Crie um novo lançamento de ${TYPES.find((t) => t.value === tab)?.label.toLowerCase()} para esta competência.`}
                action={<Button onClick={openNew} className="btn-gold">Criar Lançamento</Button>}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Situação</th>
                    <th>Descrição</th>
                    <th>Empresa</th>
                    <th>Fonte de Receita</th>
                    <th>Centro de Custo</th>
                    <th>Categoria</th>
                    <th className="text-right">Valor</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const co = companies.find((c) => c.id === e.company_id);
                    const pj = projects.find((p) => p.id === e.project_id);
                    const cc = costCenters.find((c) => c.id === e.cost_center_id);
                    const cat = categories.find((c) => c.id === e.category_id);
                    const locked = isLocked(e.company_id);
                    const isProvisioned = e.kind === 'provisionado';
                    return (
                      <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                        <td className="font-mono text-zinc-300 whitespace-nowrap">{fmtDate(e.date)}</td>
                        <td>
                          {isProvisioned ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-950/40 text-amber-300 border border-amber-700/40 uppercase tracking-wider" data-testid={`badge-provisionado-${e.id}`}>
                              Provisionado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-950/40 text-emerald-300 border border-emerald-700/40 uppercase tracking-wider" data-testid={`badge-realizado-${e.id}`}>
                              Realizado
                            </span>
                          )}
                        </td>
                        <td className="text-zinc-100">{e.description}</td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-4 rounded-sm" style={{ background: co?.color || '#888' }} />
                            <span className="text-zinc-300 text-xs">{co?.name || '-'}</span>
                          </div>
                        </td>
                        <td className="text-zinc-400 text-xs">{pj?.name || '—'}</td>
                        <td className="text-zinc-400 text-xs">{cc?.name || '—'}</td>
                        <td className="text-zinc-400 text-xs">{cat?.name || '—'}</td>
                        <td className={`text-right font-mono ${tab === 'RECEITA' ? 'emerald-text' : 'text-zinc-100'} font-semibold`}>
                          {fmtBRL(e.amount)}
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!isProvisioned && (
                              <button
                                onClick={() => openEdit(e)}
                                className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors"
                                disabled={locked}
                                title={locked ? 'Competência travada' : 'Editar'}
                                data-testid={`btn-edit-${e.id}`}
                              >
                                <Pencil size={13} />
                              </button>
                            )}
                            <button
                              onClick={() => remove(e)}
                              className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400 transition-colors disabled:opacity-40"
                              disabled={locked}
                              title={locked ? 'Competência travada' : (isProvisioned ? 'Excluir provisionamento' : 'Excluir')}
                              data-testid={`btn-delete-${e.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-zinc-900/40 font-semibold">
                    <td colSpan={7} className="text-zinc-400 uppercase text-xs tracking-widest">Total</td>
                    <td className="text-right font-mono gold-text">{fmtBRL(total)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Dialog Novo/Editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-entry">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-50">
              {editId ? 'Editar Lançamento' : 'Novo Lançamento'}
            </DialogTitle>
          </DialogHeader>

          {/* TOGGLE Realizado / Provisionado - destaque visual */}
          {!editId && (
            <div className="grid grid-cols-2 gap-2 mb-2" data-testid="realized-toggle">
              <button
                type="button"
                onClick={() => setForm({ ...form, realized: true, status: 'PAGO' })}
                className={`p-3 rounded-md border transition-all ${form.realized ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-500 hover:text-zinc-300'}`}
                data-testid="btn-realized"
              >
                <div className="text-xs uppercase tracking-wider font-semibold">Realizado</div>
                <div className="text-[11px] mt-0.5 opacity-80">Já pago / recebido — entra no Fluxo</div>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, realized: false, status: 'PENDENTE' })}
                className={`p-3 rounded-md border transition-all ${!form.realized ? 'border-amber-500/60 bg-amber-950/30 text-amber-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-500 hover:text-zinc-300'}`}
                data-testid="btn-provisionado"
              >
                <div className="text-xs uppercase tracking-wider font-semibold">Provisionado</div>
                <div className="text-[11px] mt-0.5 opacity-80">Vira Conta a {form.type === 'RECEITA' ? 'Receber' : 'Pagar'}</div>
              </button>
            </div>
          )}

          {/* TOGGLE Rateio */}
          {!editId && (
            <label
              className={`flex items-start gap-2 text-xs cursor-pointer p-3 rounded border transition-all mb-2 ${form.is_allocated ? 'border-purple-500/60 bg-purple-950/30 text-purple-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:text-zinc-200'}`}
              data-testid="rateio-toggle"
            >
              <input
                type="checkbox"
                checked={form.is_allocated}
                onChange={(e) => {
                  const isAllocated = e.target.checked;
                  setForm({
                    ...form,
                    is_allocated: isAllocated,
                    parts: isAllocated && form.parts.length === 0
                      ? [{ company_id: '', percent: '', amount: '' }, { company_id: '', percent: '', amount: '' }]
                      : form.parts,
                  });
                }}
                className="mt-0.5"
                data-testid="rateio-checkbox"
              />
              <div>
                <div className="font-semibold uppercase tracking-wider">Este lançamento é RATEADO</div>
                <div className="text-[11px] mt-0.5 opacity-80">
                  Divide o valor total entre múltiplas empresas. O lançamento "original" NÃO é criado — só os rateios.
                </div>
              </div>
            </label>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category_id: '', expense_kind: v === 'DESPESA' ? form.expense_kind : '' })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} className="focus:bg-zinc-800">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Classificação Fixa/Variável (apenas para DESPESA) */}
            {form.type === 'DESPESA' && !form.is_allocated && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Classificação da Despesa</Label>
                <Select
                  value={form.expense_kind || 'none'}
                  onValueChange={(v) => setForm({ ...form, expense_kind: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-expense-kind">
                    <SelectValue placeholder="Não classificada" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="none" className="focus:bg-zinc-800 text-zinc-400">Não classificada</SelectItem>
                    <SelectItem value="FIXA" className="focus:bg-zinc-800">Fixa (recorrente)</SelectItem>
                    <SelectItem value="VARIAVEL" className="focus:bg-zinc-800">Variável (pontual)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {!form.is_allocated && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, project_id: '', cost_center_id: '' })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-company">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-800">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* PARTES DO RATEIO */}
            {form.is_allocated && (
              <div className="col-span-2 border border-purple-700/40 bg-purple-950/20 rounded-md p-3 space-y-3" data-testid="rateio-section">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-purple-200 font-semibold">
                    Partes do Rateio ({form.parts.length})
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, allocation_mode: 'PERCENT' })}
                      className={`px-2.5 py-1 text-xs rounded ${form.allocation_mode === 'PERCENT' ? 'bg-purple-600 text-white' : 'bg-zinc-900 text-zinc-400'}`}
                      data-testid="rateio-mode-percent"
                    >% Percentual</button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, allocation_mode: 'AMOUNT' })}
                      className={`px-2.5 py-1 text-xs rounded ${form.allocation_mode === 'AMOUNT' ? 'bg-purple-600 text-white' : 'bg-zinc-900 text-zinc-400'}`}
                      data-testid="rateio-mode-amount"
                    >R$ Valor Fixo</button>
                  </div>
                </div>
                {form.parts.map((part, idx) => (
                  <div key={idx} className="flex items-end gap-2" data-testid={`rateio-part-${idx}`}>
                    <div className="flex-1">
                      {idx === 0 && <Label className="text-purple-200 text-[10px] uppercase tracking-wider">Empresa</Label>}
                      <Select
                        value={part.company_id}
                        onValueChange={(v) => {
                          const parts = [...form.parts];
                          parts[idx] = { ...part, company_id: v };
                          setForm({ ...form, parts });
                        }}
                      >
                        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid={`rateio-part-company-${idx}`}>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-60">
                          {companies.filter((c) => !form.parts.some((p, i) => i !== idx && p.company_id === c.id)).map((c) => (
                            <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-800">{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      {idx === 0 && (
                        <Label className="text-purple-200 text-[10px] uppercase tracking-wider">
                          {form.allocation_mode === 'PERCENT' ? '% Percentual' : 'Valor (R$)'}
                        </Label>
                      )}
                      <Input
                        type="number"
                        step="0.01"
                        value={form.allocation_mode === 'PERCENT' ? (part.percent || '') : (part.amount || '')}
                        onChange={(e) => {
                          const parts = [...form.parts];
                          parts[idx] = {
                            ...part,
                            [form.allocation_mode === 'PERCENT' ? 'percent' : 'amount']: e.target.value,
                          };
                          setForm({ ...form, parts });
                        }}
                        className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 font-mono"
                        placeholder="0,00"
                        data-testid={`rateio-part-value-${idx}`}
                      />
                    </div>
                    {form.parts.length > 2 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const parts = form.parts.filter((_, i) => i !== idx);
                          setForm({ ...form, parts });
                        }}
                        className="border-red-700/40 text-red-400 mb-0"
                        data-testid={`rateio-part-remove-${idx}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1 text-xs">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setForm({ ...form, parts: [...form.parts, { company_id: '', percent: '', amount: '' }] })}
                    className="border-purple-700/40 text-purple-300 gap-1"
                    data-testid="rateio-add-part"
                  >
                    <Plus size={12} /> Adicionar empresa
                  </Button>
                  <div className="text-purple-200">
                    {form.allocation_mode === 'PERCENT' ? (
                      <>Soma: <strong className="font-mono">
                        {form.parts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0).toFixed(2)}%
                      </strong> {Math.abs(form.parts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0) - 100) < 0.01 ? '✓' : '(precisa 100%)'}</>
                    ) : (
                      <>Soma: <strong className="font-mono">
                        {fmtBRL(form.parts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0))}
                      </strong> de <strong className="font-mono">{fmtBRL(parseFloat(form.amount) || 0)}</strong></>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrição *</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
                placeholder="Ex: Receita Curso Presencial SC LEG"
                data-testid="input-description"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor (R$) *</Label>
              <Input
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 font-mono"
                placeholder="0,00"
                data-testid="input-amount"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value, competence: e.target.value.slice(0, 7) })}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 font-mono"
                data-testid="input-date"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Competência</Label>
              <Input
                value={form.competence}
                onChange={(e) => setForm({ ...form, competence: e.target.value })}
                placeholder="YYYY-MM"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 font-mono"
                data-testid="input-competence"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="PENDENTE">Pendente</SelectItem>
                  <SelectItem value="PAGO">Pago</SelectItem>
                  <SelectItem value="CONCILIADO">Conciliado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fonte de Receita</Label>
              <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-project">
                  <SelectValue placeholder={form.company_id ? 'Selecione' : 'Escolha a empresa primeiro'} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  {filteredProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="focus:bg-zinc-800">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Centro de Custo</Label>
              <Select value={form.cost_center_id || 'none'} onValueChange={(v) => setForm({ ...form, cost_center_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-cost-center">
                  <SelectValue placeholder={form.company_id ? 'Selecione' : 'Escolha a empresa primeiro'} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  {filteredCC.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-800">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Categoria</Label>
              <Select value={form.category_id || 'none'} onValueChange={(v) => setForm({ ...form, category_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  {filteredCats.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-800">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Observações</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
                rows={2}
                data-testid="input-notes"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900">
              Cancelar
            </Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-entry">
              {editId ? 'Atualizar' : 'Criar Lançamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
